import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(form.email, form.password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back!");
      const role = res.user.role;
      nav(role === "admin" ? "/admin" : role === "club" ? "/club/dashboard" : role === "referee" ? "/referee/dashboard" : "/dashboard");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-[calc(100vh-80px)] grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-jpa-ink">
        <img src="https://images.unsplash.com/photo-1595435934249-5df7ed86e1c0?w=1200" alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-jpa-ink via-transparent to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <span className="bg-jpa-accent text-jpa-ink px-3 py-1 text-xs font-bold uppercase tracking-widest skew-badge w-fit">Welcome back</span>
          <h1 className="font-display text-7xl tracking-wider mt-4">SERVE.<br />DINK.<br />WIN.</h1>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 lg:p-16">
        <form onSubmit={submit} className="w-full max-w-md">
          <div className="text-xs font-bold uppercase tracking-widest text-jpa-primary">Log in</div>
          <h2 className="font-display text-5xl tracking-wider mt-1">Enter the court</h2>
          <p className="text-jpa-muted mt-2 text-sm">Sign in to manage your player profile and register for tournaments.</p>
          {sp.get("err") === "oauth" && <div className="mt-4 text-sm text-jpa-live">Sign-in failed. Please try again.</div>}

          <div className="mt-6">
            <Label>Email</Label>
            <Input required type="email" data-testid="login-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm" />
          </div>
          <div className="mt-4">
            <Label>Password</Label>
            <PasswordInput
              required
              data-testid="login-password"
              toggleTestId="login-password-toggle"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-1 rounded-sm"
            />
          </div>
          <Button disabled={loading} type="submit" data-testid="login-submit" className="w-full mt-6 bg-jpa-primary hover:bg-jpa-primary-hover text-white font-bold uppercase tracking-widest rounded-sm h-12">
            {loading ? "Signing in..." : "Log in"}
          </Button>

          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-jpa-muted">
            <div className="h-px bg-jpa-border flex-1" /> new player <div className="h-px bg-jpa-border flex-1" />
          </div>

          <button
            type="button"
            onClick={() => nav("/tournaments")}
            data-testid="login-register-with-jpa"
            className="w-full h-12 border-2 border-jpa-primary text-jpa-primary rounded-sm flex items-center justify-center gap-2 font-bold uppercase tracking-widest text-sm hover:bg-jpa-primary hover:text-white transition-colors"
          >
            <UserPlus className="w-4 h-4" /> Register for Tournament
          </button>
        </form>
      </div>
    </div>
  );
}
